/* eslint-disable react/no-unknown-property */
// analytics-event-select.tsx — PSTUDIO-4784
import React from 'react';
import { Select, SelectOption } from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';

import { WORKFLOWS_NS } from '../../../utils/i18n-namespaces';
import {
  CUSTOM_EVENTS_LIBRARY,
  fromSelectOptionValue,
  toSelectOptionValue,
} from './analytics-events';

/**
 * The design-system select for an Analytics Events cell: the Custom Events Library events as
 * options, and — through `allow-custom-value` — a name of the user's own typed
 * into the same field. Used by the Expanded Cell View panel and by the grid
 * cell, so both offer the same list and write through the same helper.
 *
 * `onChoose` is called once per commit, with a trimmed non-empty name: the select
 * emits `change` on a pick, on Enter, and when the field loses focus with new
 * text in it — never per keystroke. An emptied field writes nothing (the select
 * puts the committed text back).
 *
 * `currentName` is what the field shows when nothing is half-typed. It may be a
 * name outside the list: the select keeps such a value instead of clearing it.
 *
 * For assistive technology: a select with no visible `label` (the grid cell) is
 * named by the same translated "Event" text instead, and `errorMessage` — the
 * cell's validation message, when it has one — marks the field invalid and is
 * read after its name. The message is passed INTO the select because the node
 * that carries it elsewhere cannot be referenced from inside the select's shadow
 * root. Typing announces how many options are left, in the user's language.
 */
export const AnalyticsEventSelect: React.FC<{
  currentName: string;
  onChoose: (name: string) => void;
  testId: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  variant?: 'standard' | 'outlined';
  label?: string;
  helpText?: string;
  placeholder?: string;
  errorMessage?: string;
}> = ({
  currentName,
  onChoose,
  testId,
  disabled,
  size = 'medium',
  variant = 'outlined',
  label,
  helpText,
  placeholder,
  errorMessage,
}) => {
  const [t] = useTranslation();

  return (
    <Select
      test-id={testId}
      value={toSelectOptionValue(currentName)}
      allowCustomValue
      size={size}
      variant={variant}
      disabled={disabled}
      label={label}
      accessibleLabel={
        label
          ? undefined
          : `${t(`${WORKFLOWS_NS}:analytics_event_picker.label`)}`
      }
      accessibleDescription={errorMessage}
      fieldInvalid={!!errorMessage}
      resultsMessage={`${t(`${WORKFLOWS_NS}:analytics_event_picker.results_count`)}`}
      noResultsMessage={`${t(`${WORKFLOWS_NS}:analytics_event_picker.no_results`)}`}
      helpText={helpText}
      placeholder={placeholder}
      placement="bottom-start"
      style={{ width: '100%' }}
      onChange={(e) => {
        const name = String(
          (e.target as unknown as { value?: string | string[] }).value ?? ''
        ).trim();
        if (name) onChoose(fromSelectOptionValue(name));
      }}
    >
      {CUSTOM_EVENTS_LIBRARY.map((name) => (
        <SelectOption key={name} value={toSelectOptionValue(name)}>
          {name}
        </SelectOption>
      ))}
    </Select>
  );
};

/** The panel's field: the select with its translated label, hint and placeholder. */
export const AnalyticsEventPanelSelect: React.FC<{
  currentName: string;
  onChoose: (name: string) => void;
  disabled?: boolean;
  errorMessage?: string;
}> = ({ currentName, onChoose, disabled, errorMessage }) => {
  const [t] = useTranslation();

  return (
    <AnalyticsEventSelect
      testId="ps-expanded-cell-view-analytics-event-select"
      currentName={currentName}
      onChoose={onChoose}
      disabled={disabled}
      errorMessage={errorMessage}
      label={`${t(`${WORKFLOWS_NS}:analytics_event_picker.label`)}`}
      helpText={`${t(`${WORKFLOWS_NS}:analytics_event_picker.hint`)}`}
      placeholder={`${t(`${WORKFLOWS_NS}:analytics_event_picker.placeholder`)}`}
    />
  );
};
